import type { WalletClient } from "viem";

/**
 * Identity check performed against the WALLET ITSELF, immediately before a
 * transaction is signed.
 *
 * Every other identity signal in the app is a cache:
 *  - `useAccount().address` is wagmi's cached view of the connection
 *  - `useAuth().user.wallet_address` is the wallet the JWT was minted for
 *
 * Both can be stale at the same time, and when they are, they agree with each
 * other — so comparing them to one another proves nothing. That is exactly how
 * a buyer ended up placed under the wrong upline: MetaMask had switched
 * accounts, wagmi had not noticed, the session still described the old account,
 * every guard passed, and `msg.sender` was a third thing entirely.
 *
 * `walletClient.getAddresses()` issues a live `eth_accounts` to the provider,
 * so it returns who will actually sign. Comparing the cached values against
 * that is the only check that cannot be fooled by staleness.
 *
 * Placement is one-shot and irreversible, so any disagreement is fatal here —
 * we refuse rather than guess.
 */
export const assertLiveSigner = async ({
  walletClient,
  cachedAddress,
  sessionWallet,
  mismatchMessage,
}: {
  walletClient: Pick<WalletClient, "getAddresses"> | undefined | null;
  /** wagmi's cached connected address. */
  cachedAddress: string;
  /** Wallet the JWT belongs to, if signed in. */
  sessionWallet: string | undefined;
  /** Localised message shown when identities disagree. */
  mismatchMessage: string;
}): Promise<`0x${string}`> => {
  if (!walletClient) throw new Error(mismatchMessage);

  const [signer] = await walletClient.getAddresses();
  if (!signer) throw new Error(mismatchMessage);

  const live = signer.toLowerCase();

  // Stale wagmi: the wallet switched accounts and the app never noticed.
  if (live !== cachedAddress.toLowerCase()) throw new Error(mismatchMessage);

  // Stale session: signed in as one wallet, signing with another.
  if (sessionWallet && live !== sessionWallet.toLowerCase()) {
    throw new Error(mismatchMessage);
  }

  return signer;
};
